import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon, Globe, Mail, Shield, Server, Save,
  RefreshCw, Eye, EyeOff, CheckCircle, XCircle, AlertTriangle,
  Building2, Clock, Send, BarChart3, Palette, Image, Type, Upload, HardDrive,
  Plug, Wand2, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { toast } from 'sonner';
import ImagePicker from '@/components/ImagePicker';
import { useAppTheme } from '@/contexts/AppThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export default function AppSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const { refreshAppTheme } = useAppTheme();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/settings`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
      } else {
        toast.error(data.detail || 'Failed to load settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings),
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Settings saved successfully!');
        setSettings(data.data);
        // Refresh the app theme to apply changes immediately
        refreshAppTheme();
      } else {
        toast.error(data.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testEmailSettings = async () => {
    setTestingEmail(true);
    setEmailTestResult(null);
    try {
      const response = await fetch(`${API_URL}/api/v1/settings/email/test`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setEmailTestResult(data.data);
      }
    } catch (error) {
      setEmailTestResult({ success: false, message: 'Failed to test connection' });
    } finally {
      setTestingEmail(false);
    }
  };

  const updateSetting = (section, key, value) => {
    setSettings({
      ...settings,
      [section]: {
        ...settings[section],
        [key]: value,
      },
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const PasswordInput = ({ section, field, label, placeholder }) => {
    const value = settings?.[section]?.[field] || '';
    const isVisible = showPasswords[field];
    const isMasked = value === '••••••••';

    return (
      <div className="space-y-1">
        <Label>{label}</Label>
        <div className="relative">
          <Input
            type={isVisible ? 'text' : 'password'}
            value={value}
            onChange={(e) => updateSetting(section, field, e.target.value)}
            placeholder={placeholder}
            className="pr-10"
            data-testid={`settings-${section}-${field}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => togglePasswordVisibility(field)}
          >
            {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
        {isMasked && (
          <p className="text-xs text-muted-foreground">
            Leave as-is to keep existing value, or enter a new value
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="app-settings-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-[500px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="app-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <SettingsIcon className="h-6 w-6" />
            Application Settings
          </h1>
          <p className="text-muted-foreground">
            Configure application settings and integrations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchSettings} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Settings'}
          </Button>
        </div>
      </div>

      {settings?.updated_at && (
        <p className="text-sm text-muted-foreground">
          Last updated: {new Date(settings.updated_at).toLocaleString()}
          {settings.updated_by && ` by ${settings.updated_by}`}
        </p>
      )}

      {/* Settings Tabs */}
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid grid-cols-9 w-fit">
          <TabsTrigger value="general" className="gap-2">
            <Building2 className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="media" className="gap-2">
            <HardDrive className="h-4 w-4" />
            Media
          </TabsTrigger>
          <TabsTrigger value="theme" className="gap-2">
            <Palette className="h-4 w-4" />
            Theme
          </TabsTrigger>
          <TabsTrigger value="urls" className="gap-2">
            <Globe className="h-4 w-4" />
            URLs
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="email" className="gap-2">
            <Mail className="h-4 w-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="h-4 w-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="ftp" className="gap-2">
            <Server className="h-4 w-4" />
            FTP Import
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2">
            <Plug className="h-4 w-4" />
            Integrations
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>Basic application configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Application Name</Label>
                  <Input
                    value={settings?.general?.app_name || ''}
                    onChange={(e) => updateSetting('general', 'app_name', e.target.value)}
                    placeholder="LinkHub"
                    data-testid="settings-general-app-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Company Name</Label>
                  <Input
                    value={settings?.general?.company_name || ''}
                    onChange={(e) => updateSetting('general', 'company_name', e.target.value)}
                    placeholder="Your Company"
                    data-testid="settings-general-company-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Support Email</Label>
                  <Input
                    type="email"
                    value={settings?.general?.support_email || ''}
                    onChange={(e) => updateSetting('general', 'support_email', e.target.value)}
                    placeholder="support@company.com"
                    data-testid="settings-general-support-email"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Default Timezone</Label>
                  <Select
                    value={settings?.general?.default_timezone || 'UTC'}
                    onValueChange={(v) => updateSetting('general', 'default_timezone', v)}
                  >
                    <SelectTrigger data-testid="settings-general-timezone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="Africa/Johannesburg">Africa/Johannesburg (SAST)</SelectItem>
                      <SelectItem value="Europe/London">Europe/London (GMT/BST)</SelectItem>
                      <SelectItem value="America/New_York">America/New_York (EST/EDT)</SelectItem>
                      <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</SelectItem>
                      <SelectItem value="Asia/Singapore">Asia/Singapore (SGT)</SelectItem>
                      <SelectItem value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Date Format</Label>
                  <Select
                    value={settings?.general?.date_format || 'YYYY-MM-DD'}
                    onValueChange={(v) => updateSetting('general', 'date_format', v)}
                  >
                    <SelectTrigger data-testid="settings-general-date-format">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="YYYY-MM-DD">2024-12-31 (ISO)</SelectItem>
                      <SelectItem value="DD/MM/YYYY">31/12/2024 (UK/EU)</SelectItem>
                      <SelectItem value="MM/DD/YYYY">12/31/2024 (US)</SelectItem>
                      <SelectItem value="DD MMM YYYY">31 Dec 2024</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Analytics Data Retention (days)</Label>
                  <Input
                    type="number"
                    min="30"
                    max="3650"
                    value={settings?.general?.analytics_retention_days || 365}
                    onChange={(e) => updateSetting('general', 'analytics_retention_days', parseInt(e.target.value) || 365)}
                    data-testid="settings-general-retention"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Media Settings */}
        <TabsContent value="media">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Media Upload Settings
              </CardTitle>
              <CardDescription>
                Configure file upload limits and optimization defaults for the Media Library
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Upload Limits */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Upload Limits</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="max-file-size">Maximum File Size (MB)</Label>
                    <Input
                      id="max-file-size"
                      type="number"
                      min="1"
                      max="500"
                      value={settings?.media?.max_file_size_mb || 10}
                      onChange={(e) => updateSetting('media', 'max_file_size_mb', parseInt(e.target.value) || 10)}
                      data-testid="settings-media-max-file-size"
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum size for each individual file upload
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max-files">Maximum Files Per Batch</Label>
                    <Input
                      id="max-files"
                      type="number"
                      min="1"
                      max="500"
                      value={settings?.media?.max_files_per_batch || 50}
                      onChange={(e) => updateSetting('media', 'max_files_per_batch', parseInt(e.target.value) || 50)}
                      data-testid="settings-media-max-files"
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum number of files allowed in a single batch upload
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Image Optimization Defaults */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Image Optimization Defaults</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Optimize Images by Default</Label>
                      <p className="text-xs text-muted-foreground">
                        Automatically resize large images to Full HD (1920×1080) maintaining aspect ratio
                      </p>
                    </div>
                    <Switch
                      checked={settings?.media?.optimize_by_default ?? true}
                      onCheckedChange={(checked) => updateSetting('media', 'optimize_by_default', checked)}
                      data-testid="settings-media-optimize-default"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="output-format">Default Output Format</Label>
                    <Select
                      value={settings?.media?.default_output_format || 'webp'}
                      onValueChange={(value) => updateSetting('media', 'default_output_format', value)}
                    >
                      <SelectTrigger id="output-format" className="w-[300px]" data-testid="settings-media-format">
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="webp">WebP (Best compression, ~30% smaller)</SelectItem>
                        <SelectItem value="jpeg">JPEG (Good compression, universal support)</SelectItem>
                        <SelectItem value="original">Original (Keep source format, resize only)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Format used when optimization is enabled. WebP provides best compression with automatic JPEG fallback for older browsers.
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Info Box */}
              <Alert>
                <HardDrive className="h-4 w-4" />
                <AlertTitle>Storage Information</AlertTitle>
                <AlertDescription>
                  <p className="text-sm">
                    These settings apply to all users uploading files through the Media Library.
                    Changes take effect immediately for new uploads.
                  </p>
                  <p className="text-sm mt-2">
                    <strong>Allowed file types:</strong> Images (JPEG, PNG, GIF, WebP, SVG), 
                    Videos (MP4, WebM, OGG), Documents (PDF, DOC, DOCX, CSV)
                  </p>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Theme Settings */}
        <TabsContent value="theme">
          <div className="space-y-6">
            {/* Login Page Theme */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Login Page Theme
                </CardTitle>
                <CardDescription>Customize the appearance of the login page</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Login Logo</Label>
                      <ImagePicker
                        value={settings?.theme?.login_logo || ''}
                        onChange={(url) => updateSetting('theme', 'login_logo', url)}
                        placeholder="Select or upload login logo"
                      />
                      <p className="text-xs text-muted-foreground">Displayed at the top of the login form</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Background Image</Label>
                      <ImagePicker
                        value={settings?.theme?.login_background || ''}
                        onChange={(url) => updateSetting('theme', 'login_background', url)}
                        placeholder="Select or upload background"
                      />
                      <p className="text-xs text-muted-foreground">Full-screen background for login page</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Background Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings?.theme?.login_bg_color || '#f8fafc'}
                          onChange={(e) => updateSetting('theme', 'login_bg_color', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={settings?.theme?.login_bg_color || '#f8fafc'}
                          onChange={(e) => updateSetting('theme', 'login_bg_color', e.target.value)}
                          placeholder="#f8fafc"
                          className="flex-1"
                          data-testid="settings-theme-login-bg"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Fallback if no background image</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Card Background Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings?.theme?.login_card_bg || '#ffffff'}
                          onChange={(e) => updateSetting('theme', 'login_card_bg', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={settings?.theme?.login_card_bg || '#ffffff'}
                          onChange={(e) => updateSetting('theme', 'login_card_bg', e.target.value)}
                          placeholder="#ffffff"
                          className="flex-1"
                          data-testid="settings-theme-login-card"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Welcome Text</Label>
                      <Input
                        value={settings?.theme?.login_welcome_text || ''}
                        onChange={(e) => updateSetting('theme', 'login_welcome_text', e.target.value)}
                        placeholder="Welcome to LinkHub"
                        data-testid="settings-theme-login-welcome"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Subtitle Text</Label>
                      <Input
                        value={settings?.theme?.login_subtitle || ''}
                        onChange={(e) => updateSetting('theme', 'login_subtitle', e.target.value)}
                        placeholder="Sign in to your account"
                        data-testid="settings-theme-login-subtitle"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Admin Theme */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Admin Panel Theme
                </CardTitle>
                <CardDescription>Customize colors and styling for the admin interface</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Admin Logo</Label>
                      <ImagePicker
                        value={settings?.theme?.admin_logo || ''}
                        onChange={(url) => updateSetting('theme', 'admin_logo', url)}
                        placeholder="Select or upload admin logo"
                      />
                      <p className="text-xs text-muted-foreground">Displayed in the sidebar</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Admin Logo (Dark Mode)</Label>
                      <ImagePicker
                        value={settings?.theme?.admin_logo_dark || ''}
                        onChange={(url) => updateSetting('theme', 'admin_logo_dark', url)}
                        placeholder="Select or upload dark mode logo"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Favicon</Label>
                      <ImagePicker
                        value={settings?.theme?.favicon || ''}
                        onChange={(url) => updateSetting('theme', 'favicon', url)}
                        placeholder="Select or upload favicon"
                      />
                      <p className="text-xs text-muted-foreground">Browser tab icon (recommended: 32x32 or 64x64)</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-medium">Color Palette</h4>
                    
                    <div className="space-y-2">
                      <Label>Primary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings?.theme?.primary_color || '#154734'}
                          onChange={(e) => updateSetting('theme', 'primary_color', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={settings?.theme?.primary_color || '#154734'}
                          onChange={(e) => updateSetting('theme', 'primary_color', e.target.value)}
                          placeholder="#154734"
                          className="flex-1"
                          data-testid="settings-theme-primary"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Used for buttons, links, and accents</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Secondary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings?.theme?.secondary_color || '#ba0c2f'}
                          onChange={(e) => updateSetting('theme', 'secondary_color', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={settings?.theme?.secondary_color || '#ba0c2f'}
                          onChange={(e) => updateSetting('theme', 'secondary_color', e.target.value)}
                          placeholder="#ba0c2f"
                          className="flex-1"
                          data-testid="settings-theme-secondary"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Sidebar Background</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings?.theme?.sidebar_bg || '#1f2937'}
                          onChange={(e) => updateSetting('theme', 'sidebar_bg', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={settings?.theme?.sidebar_bg || '#1f2937'}
                          onChange={(e) => updateSetting('theme', 'sidebar_bg', e.target.value)}
                          placeholder="#1f2937"
                          className="flex-1"
                          data-testid="settings-theme-sidebar"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Sidebar Text Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings?.theme?.sidebar_text || '#ffffff'}
                          onChange={(e) => updateSetting('theme', 'sidebar_text', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={settings?.theme?.sidebar_text || '#ffffff'}
                          onChange={(e) => updateSetting('theme', 'sidebar_text', e.target.value)}
                          placeholder="#ffffff"
                          className="flex-1"
                          data-testid="settings-theme-sidebar-text"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Type className="h-4 w-4" />
                      Typography
                    </h4>
                    
                    <div className="space-y-2">
                      <Label>Heading Font</Label>
                      <Select
                        value={settings?.theme?.heading_font || 'Montserrat'}
                        onValueChange={(v) => updateSetting('theme', 'heading_font', v)}
                      >
                        <SelectTrigger data-testid="settings-theme-heading-font">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Montserrat">Montserrat</SelectItem>
                          <SelectItem value="Inter">Inter</SelectItem>
                          <SelectItem value="Roboto">Roboto</SelectItem>
                          <SelectItem value="Figtree">Figtree</SelectItem>
                          <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                          <SelectItem value="Source Sans Pro">Source Sans Pro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Body Font</Label>
                      <Select
                        value={settings?.theme?.body_font || 'Inter'}
                        onValueChange={(v) => updateSetting('theme', 'body_font', v)}
                      >
                        <SelectTrigger data-testid="settings-theme-body-font">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Inter">Inter</SelectItem>
                          <SelectItem value="Roboto">Roboto</SelectItem>
                          <SelectItem value="Open Sans">Open Sans</SelectItem>
                          <SelectItem value="Lato">Lato</SelectItem>
                          <SelectItem value="Source Sans Pro">Source Sans Pro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-medium">Options</h4>
                    
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="font-normal">Enable Dark Mode Toggle</Label>
                        <p className="text-xs text-muted-foreground">Allow users to switch between light/dark themes</p>
                      </div>
                      <Switch
                        checked={settings?.theme?.enable_dark_mode !== false}
                        onCheckedChange={(v) => updateSetting('theme', 'enable_dark_mode', v)}
                        data-testid="settings-theme-dark-mode"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="font-normal">Compact Mode</Label>
                        <p className="text-xs text-muted-foreground">Reduce spacing for more content density</p>
                      </div>
                      <Switch
                        checked={settings?.theme?.compact_mode || false}
                        onCheckedChange={(v) => updateSetting('theme', 'compact_mode', v)}
                        data-testid="settings-theme-compact"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="font-normal">Show Branding in Footer</Label>
                        <p className="text-xs text-muted-foreground">Display "Powered by LinkHub" in admin footer</p>
                      </div>
                      <Switch
                        checked={settings?.theme?.show_branding !== false}
                        onCheckedChange={(v) => updateSetting('theme', 'show_branding', v)}
                        data-testid="settings-theme-branding"
                      />
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <Label>Custom CSS</Label>
                  <Textarea
                    value={settings?.theme?.custom_css || ''}
                    onChange={(e) => updateSetting('theme', 'custom_css', e.target.value)}
                    placeholder="/* Add custom CSS here */"
                    rows={6}
                    className="font-mono text-sm"
                    data-testid="settings-theme-custom-css"
                  />
                  <p className="text-xs text-muted-foreground">
                    Advanced: Add custom CSS that will be applied to the admin interface
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* URL Settings */}
        <TabsContent value="urls">
          <Card>
            <CardHeader>
              <CardTitle>URL Configuration</CardTitle>
              <CardDescription>Configure the URLs for admin and public-facing services</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Important</AlertTitle>
                <AlertDescription>
                  These URLs determine where your services are accessible. The admin panel will only be available 
                  on the Admin URL. Changing URLs may require DNS configuration changes.
                </AlertDescription>
              </Alert>

              <div className="space-y-1">
                <Label>Admin URL</Label>
                <Input
                  value={settings?.urls?.admin_url || ''}
                  onChange={(e) => updateSetting('urls', 'admin_url', e.target.value)}
                  placeholder="https://app.yourdomain.com"
                  data-testid="settings-urls-admin"
                />
                <p className="text-xs text-muted-foreground">
                  Where the admin panel is accessible. Only internal users should access this URL.
                </p>
              </div>

              <Separator />
              <h4 className="font-medium">Public URLs</h4>

              <div className="space-y-1">
                <Label>Redirects URL</Label>
                <Input
                  value={settings?.urls?.redirects_url || ''}
                  onChange={(e) => updateSetting('urls', 'redirects_url', e.target.value)}
                  placeholder="https://go.yourdomain.com"
                  data-testid="settings-urls-redirects"
                />
                <p className="text-xs text-muted-foreground">
                  Public URL for QR code scans and short URL redirects.
                  <br />
                  • QR codes: <code className="bg-muted px-1 rounded">https://go.yourdomain.com/r/ABC123</code>
                  <br />
                  • Short URLs: <code className="bg-muted px-1 rounded">https://go.yourdomain.com/go/xyz</code>
                </p>
              </div>

              <div className="space-y-1">
                <Label>Pages URL</Label>
                <Input
                  value={settings?.urls?.pages_url || ''}
                  onChange={(e) => updateSetting('urls', 'pages_url', e.target.value)}
                  placeholder="https://pages.yourdomain.com"
                  data-testid="settings-urls-pages"
                />
                <p className="text-xs text-muted-foreground">
                  Public URL for landing pages and bio pages.
                  <br />
                  • Landing pages: <code className="bg-muted px-1 rounded">https://pages.yourdomain.com/p/page-slug</code>
                  <br />
                  • Bio pages: <code className="bg-muted px-1 rounded">https://pages.yourdomain.com/b/username</code>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Settings (GTM) */}
        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Google Tag Manager Integration</CardTitle>
              <CardDescription>
                Manage all your marketing tags (GA4, Facebook Pixel, LinkedIn, etc.) through a single GTM container
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Google Tag Manager</Label>
                  <p className="text-xs text-muted-foreground">
                    Push events to GTM dataLayer for all tracked activities
                  </p>
                </div>
                <Switch
                  checked={settings?.analytics?.gtm_enabled || false}
                  onCheckedChange={(v) => updateSetting('analytics', 'gtm_enabled', v)}
                  data-testid="settings-gtm-enabled"
                />
              </div>

              {settings?.analytics?.gtm_enabled && (
                <>
                  <Separator />
                  <h4 className="font-medium">GTM Container</h4>
                  
                  <div className="space-y-1">
                    <Label>Container ID</Label>
                    <Input
                      value={settings?.analytics?.gtm_container_id || ''}
                      onChange={(e) => updateSetting('analytics', 'gtm_container_id', e.target.value)}
                      placeholder="GTM-XXXXXXX"
                      data-testid="settings-gtm-container-id"
                    />
                    <p className="text-xs text-muted-foreground">
                      Found in GTM: Admin → Container Settings → Container ID
                    </p>
                  </div>

                  <Alert>
                    <BarChart3 className="h-4 w-4" />
                    <AlertTitle>Configure Tags in GTM</AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p>LinkHub pushes the following custom events to the dataLayer:</p>
                      <ul className="list-disc list-inside text-xs space-y-1 mt-2">
                        <li><code className="bg-muted px-1 rounded">creativehub_page_view</code> - Landing page views</li>
                        <li><code className="bg-muted px-1 rounded">creativehub_form_submit</code> - Form submissions</li>
                        <li><code className="bg-muted px-1 rounded">creativehub_bio_page_view</code> - Bio page views</li>
                        <li><code className="bg-muted px-1 rounded">creativehub_bio_link_click</code> - Bio link clicks</li>
                        <li><code className="bg-muted px-1 rounded">creativehub_qr_scan</code> - QR code scans</li>
                        <li><code className="bg-muted px-1 rounded">creativehub_url_click</code> - Short URL clicks</li>
                      </ul>
                      <p className="mt-2">Create triggers in GTM to fire your GA4, Facebook, or other tags on these events.</p>
                    </AlertDescription>
                  </Alert>

                  <Separator />
                  <h4 className="font-medium">Event Tracking Options</h4>
                  <p className="text-xs text-muted-foreground mb-4">
                    Choose which events to push to the GTM dataLayer. Server-side tracking continues regardless of these settings.
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="font-normal">QR Code Scans</Label>
                        <p className="text-xs text-muted-foreground">Push QR scan events to dataLayer</p>
                      </div>
                      <Switch
                        checked={settings?.analytics?.track_qr_scans !== false}
                        onCheckedChange={(v) => updateSetting('analytics', 'track_qr_scans', v)}
                        data-testid="settings-analytics-track-qr"
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="font-normal">Short URL Clicks</Label>
                        <p className="text-xs text-muted-foreground">Push URL click events to dataLayer</p>
                      </div>
                      <Switch
                        checked={settings?.analytics?.track_url_clicks !== false}
                        onCheckedChange={(v) => updateSetting('analytics', 'track_url_clicks', v)}
                        data-testid="settings-analytics-track-urls"
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="font-normal">Landing Page Views</Label>
                        <p className="text-xs text-muted-foreground">Push page view events to dataLayer</p>
                      </div>
                      <Switch
                        checked={settings?.analytics?.track_page_views !== false}
                        onCheckedChange={(v) => updateSetting('analytics', 'track_page_views', v)}
                        data-testid="settings-analytics-track-pages"
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="font-normal">Form Submissions</Label>
                        <p className="text-xs text-muted-foreground">Push form submit events to dataLayer</p>
                      </div>
                      <Switch
                        checked={settings?.analytics?.track_form_submissions !== false}
                        onCheckedChange={(v) => updateSetting('analytics', 'track_form_submissions', v)}
                        data-testid="settings-analytics-track-forms"
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <Label className="font-normal">Bio Page Events</Label>
                        <p className="text-xs text-muted-foreground">Push bio view/click events to dataLayer</p>
                      </div>
                      <Switch
                        checked={settings?.analytics?.track_bio_events !== false}
                        onCheckedChange={(v) => updateSetting('analytics', 'track_bio_events', v)}
                        data-testid="settings-analytics-track-bio"
                      />
                    </div>
                  </div>

                  {/* Legacy GA4 Settings (collapsed by default) */}
                  <Separator />
                  <details className="group">
                    <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                      Legacy GA4 Direct Integration (deprecated)
                    </summary>
                    <div className="mt-4 p-4 border rounded-lg bg-muted/30 space-y-4">
                      <p className="text-xs text-muted-foreground">
                        These settings are deprecated. Configure GA4 through GTM instead for better flexibility.
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>Enable Legacy GA4</Label>
                          <p className="text-xs text-muted-foreground">Direct GA4 integration (not recommended)</p>
                        </div>
                        <Switch
                          checked={settings?.analytics?.ga4_enabled || false}
                          onCheckedChange={(v) => updateSetting('analytics', 'ga4_enabled', v)}
                          data-testid="settings-analytics-enabled"
                        />
                      </div>
                      {settings?.analytics?.ga4_enabled && (
                        <>
                          <div className="space-y-1">
                            <Label>Measurement ID</Label>
                            <Input
                              value={settings?.analytics?.ga4_measurement_id || ''}
                              onChange={(e) => updateSetting('analytics', 'ga4_measurement_id', e.target.value)}
                              placeholder="G-XXXXXXXXXX"
                              data-testid="settings-analytics-measurement-id"
                            />
                          </div>
                          <PasswordInput
                            section="analytics"
                            field="ga4_api_secret"
                            label="Measurement Protocol API Secret"
                            placeholder="Enter API secret"
                          />
                        </>
                      )}
                    </div>
                  </details>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle>Email Configuration</CardTitle>
              <CardDescription>Configure email sending for notifications and alerts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-1">
                <Label>Email Provider</Label>
                <Select
                  value={settings?.email?.provider || 'smtp'}
                  onValueChange={(v) => updateSetting('email', 'provider', v)}
                >
                  <SelectTrigger data-testid="settings-email-provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="smtp">SMTP Server</SelectItem>
                    <SelectItem value="sendgrid">SendGrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {settings?.email?.provider === 'smtp' ? (
                <>
                  <Separator />
                  <h4 className="font-medium">SMTP Settings</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label>SMTP Host</Label>
                      <Input
                        value={settings?.email?.smtp_host || ''}
                        onChange={(e) => updateSetting('email', 'smtp_host', e.target.value)}
                        placeholder="smtp.gmail.com"
                        data-testid="settings-email-smtp-host"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>SMTP Port</Label>
                      <Input
                        type="number"
                        value={settings?.email?.smtp_port || 587}
                        onChange={(e) => updateSetting('email', 'smtp_port', parseInt(e.target.value) || 587)}
                        placeholder="587"
                        data-testid="settings-email-smtp-port"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label>SMTP Username</Label>
                      <Input
                        value={settings?.email?.smtp_user || ''}
                        onChange={(e) => updateSetting('email', 'smtp_user', e.target.value)}
                        placeholder="user@gmail.com"
                        data-testid="settings-email-smtp-user"
                      />
                    </div>
                    <PasswordInput
                      section="email"
                      field="smtp_password"
                      label="SMTP Password"
                      placeholder="••••••••"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={settings?.email?.smtp_use_tls !== false}
                      onCheckedChange={(v) => updateSetting('email', 'smtp_use_tls', v)}
                      data-testid="settings-email-smtp-tls"
                    />
                    <Label>Use TLS Encryption</Label>
                  </div>
                </>
              ) : (
                <>
                  <Separator />
                  <h4 className="font-medium">SendGrid Settings</h4>
                  <PasswordInput
                    section="email"
                    field="sendgrid_api_key"
                    label="SendGrid API Key"
                    placeholder="SG.xxxxxxxx"
                  />
                </>
              )}

              <Separator />
              <h4 className="font-medium">Sender Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>From Email Address</Label>
                  <Input
                    type="email"
                    value={settings?.email?.smtp_from_email || ''}
                    onChange={(e) => updateSetting('email', 'smtp_from_email', e.target.value)}
                    placeholder="notifications@company.com"
                    data-testid="settings-email-from-email"
                  />
                </div>
                <div className="space-y-1">
                  <Label>From Name</Label>
                  <Input
                    value={settings?.email?.smtp_from_name || ''}
                    onChange={(e) => updateSetting('email', 'smtp_from_name', e.target.value)}
                    placeholder="LinkHub"
                    data-testid="settings-email-from-name"
                  />
                </div>
              </div>

              <Separator />
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={testEmailSettings}
                  disabled={testingEmail}
                  data-testid="test-email-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {testingEmail ? 'Testing...' : 'Test Email Configuration'}
                </Button>
                {emailTestResult && (
                  <div className="flex items-center gap-2">
                    {emailTestResult.success ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    <span className={emailTestResult.success ? 'text-green-600' : 'text-red-600'}>
                      {emailTestResult.message}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>Configure security and authentication options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <PasswordInput
                section="security"
                field="ip_hash_salt"
                label="IP Address Hash Salt"
                placeholder="random-salt-string"
              />
              <p className="text-xs text-muted-foreground -mt-2">
                Used to anonymize IP addresses in analytics. Change only if you want to reset all visitor tracking.
              </p>

              <Separator />

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Session Timeout (minutes)</Label>
                  <Input
                    type="number"
                    min="5"
                    max="1440"
                    value={settings?.security?.session_timeout_minutes || 60}
                    onChange={(e) => updateSetting('security', 'session_timeout_minutes', parseInt(e.target.value) || 60)}
                    data-testid="settings-security-session-timeout"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Max Login Attempts</Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={settings?.security?.max_login_attempts || 5}
                    onChange={(e) => updateSetting('security', 'max_login_attempts', parseInt(e.target.value) || 5)}
                    data-testid="settings-security-max-attempts"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Lockout Duration (minutes)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="60"
                    value={settings?.security?.lockout_duration_minutes || 15}
                    onChange={(e) => updateSetting('security', 'lockout_duration_minutes', parseInt(e.target.value) || 15)}
                    data-testid="settings-security-lockout"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FTP Import Settings */}
        <TabsContent value="ftp">
          <Card>
            <CardHeader>
              <CardTitle>FTP Import Settings</CardTitle>
              <CardDescription>Configure scheduled data imports from FTP server</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings?.ftp?.enabled || false}
                  onCheckedChange={(v) => updateSetting('ftp', 'enabled', v)}
                  data-testid="settings-ftp-enabled"
                />
                <Label>Enable Scheduled FTP Imports</Label>
              </div>

              {settings?.ftp?.enabled && (
                <>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label>FTP Host</Label>
                      <Input
                        value={settings?.ftp?.host || ''}
                        onChange={(e) => updateSetting('ftp', 'host', e.target.value)}
                        placeholder="ftp.company.com"
                        data-testid="settings-ftp-host"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>FTP Port</Label>
                      <Input
                        type="number"
                        value={settings?.ftp?.port || 21}
                        onChange={(e) => updateSetting('ftp', 'port', parseInt(e.target.value) || 21)}
                        placeholder="21"
                        data-testid="settings-ftp-port"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label>FTP Username</Label>
                      <Input
                        value={settings?.ftp?.username || ''}
                        onChange={(e) => updateSetting('ftp', 'username', e.target.value)}
                        placeholder="username"
                        data-testid="settings-ftp-username"
                      />
                    </div>
                    <PasswordInput
                      section="ftp"
                      field="password"
                      label="FTP Password"
                      placeholder="••••••••"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label>Remote Directory Path</Label>
                    <Input
                      value={settings?.ftp?.remote_path || '/'}
                      onChange={(e) => updateSetting('ftp', 'remote_path', e.target.value)}
                      placeholder="/data/exports"
                      data-testid="settings-ftp-path"
                    />
                  </div>

                  <Separator />
                  <h4 className="font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Import Schedule
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label>Hour (0-23)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        value={settings?.ftp?.schedule_hour ?? 2}
                        onChange={(e) => updateSetting('ftp', 'schedule_hour', parseInt(e.target.value) || 0)}
                        data-testid="settings-ftp-hour"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Minute (0-59)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="59"
                        value={settings?.ftp?.schedule_minute ?? 0}
                        onChange={(e) => updateSetting('ftp', 'schedule_minute', parseInt(e.target.value) || 0)}
                        data-testid="settings-ftp-minute"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Import will run daily at {String(settings?.ftp?.schedule_hour ?? 2).padStart(2, '0')}:
                    {String(settings?.ftp?.schedule_minute ?? 0).padStart(2, '0')} server time
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Settings */}
        <TabsContent value="integrations">
          <div className="space-y-6">
            {/* Future integrations placeholder */}
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-muted-foreground">More Integrations Coming Soon</CardTitle>
                <CardDescription>
                  Additional integrations (SendGrid, Twilio, etc.) will be configurable here in future updates.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
